const HIN_PATTERN = /^[A-Z0-9]{12}$/;
const MONTH_MAP: Record<string, string> = {
  A: 'January',
  B: 'February',
  C: 'March',
  D: 'April',
  E: 'May',
  F: 'June',
  G: 'July',
  H: 'August',
  I: 'September',
  J: 'October',
  K: 'November',
  L: 'December',
};

export interface HinDecodeResult {
  mic: string;
  manufacturer: string;
  serialNumber: string;
  buildMonth: string;
  modelYear: number;
  certYear: number;
}

export type HinParseResult = Omit<HinDecodeResult, 'manufacturer'>;

/** Validates and parses a HIN without resolving the manufacturer, so it doesn't need the MIC table. */
export function parseHin(hin: string): HinParseResult | null {
  const normalizedHin = normalizeHin(hin);
  if (!normalizedHin || !HIN_PATTERN.test(normalizedHin)) return null;

  const mic = normalizedHin.slice(0, 3);
  const serialNumber = normalizedHin.slice(3, 8);
  const ninthChar = normalizedHin[8];

  let buildMonth = '';
  let modelYear = 0;
  let certYear = 0;

  if (isMonthCode(ninthChar)) {
    const monthCode = ninthChar;
    const certYearDigit = parseDigit(normalizedHin[9]);
    const modelYearValue = parseTwoDigitYear(normalizedHin.slice(10, 12));

    if (certYearDigit === null || modelYearValue === null) return null;

    const resolvedModelYear = resolvePost1984ModelYear(modelYearValue);
    const month = MONTH_MAP[monthCode];

    if (!month || resolvedModelYear === null) return null;

    const resolvedCertYear = resolveCertificationYear(resolvedModelYear, certYearDigit);

    buildMonth = month;
    modelYear = resolvedModelYear;
    certYear = resolvedCertYear;
  } else {
    const certYearDigit = parseDigit(ninthChar);
    const monthCode = normalizedHin[9];
    const modelYearValue = parseTwoDigitYear(normalizedHin.slice(10, 12));

    if (certYearDigit === null || !isMonthCode(monthCode) || modelYearValue === null) return null;

    const resolvedModelYear = resolvePre1984ModelYear(modelYearValue);
    if (resolvedModelYear === null) return null;

    buildMonth = MONTH_MAP[monthCode];
    modelYear = resolvedModelYear;
    certYear = resolveCertificationYear(resolvedModelYear, certYearDigit);
  }

  return {
    mic,
    serialNumber,
    buildMonth,
    modelYear,
    certYear,
  };
}

function normalizeHin(hin: string): string {
  return (hin || '').trim().toUpperCase();
}

function isMonthCode(code: string): boolean {
  return Object.prototype.hasOwnProperty.call(MONTH_MAP, code);
}

function parseDigit(value: string): number | null {
  if (!/^\d$/.test(value)) return null;
  return parseInt(value, 10);
}

function parseTwoDigitYear(value: string): number | null {
  if (!/^\d{2}$/.test(value)) return null;
  return parseInt(value, 10);
}

function resolvePost1984ModelYear(twoDigitYear: number): number | null {
  if (twoDigitYear >= 84) return 1900 + twoDigitYear;

  const thisYear = new Date().getFullYear();
  const candidate = 2000 + twoDigitYear;

  if (candidate <= thisYear + 2) return candidate;

  const previousCenturyCandidate = 1900 + twoDigitYear;
  return previousCenturyCandidate >= 1984 ? previousCenturyCandidate : null;
}

function resolvePre1984ModelYear(twoDigitYear: number): number | null {
  if (twoDigitYear > 83) return null;
  return 1900 + twoDigitYear;
}

function resolveCertificationYear(modelYear: number, certYearDigit: number): number {
  if (modelYear % 10 === certYearDigit) return modelYear;
  if ((modelYear - 1) % 10 === certYearDigit) return modelYear - 1;

  const baseDecade = Math.floor(modelYear / 10) * 10;
  const candidate = baseDecade + certYearDigit;
  return candidate > modelYear ? candidate - 10 : candidate;
}
